import { Fragment } from "react";
import { Badge } from "../../components/ui/Badge";
import { TBody, TD, TH, THead, TR, Table } from "../../components/ui/Table";
import type { ProductSellingPlan } from "../../lib/types";
import { groupPlansByName } from "./groupPlansByName";

const CATEGORY_LABELS: Record<string, string> = {
	SUBSCRIPTION: "Subscription",
	PRE_ORDER: "Pre-order",
	TRY_BEFORE_YOU_BUY: "Try before you buy",
	OTHER: "Other",
};

const cadence = (interval: string | null, count: number | null): string => {
	if (!interval) return "One time";
	const unit = interval.toLowerCase();
	return count && count > 1 ? `Every ${count} ${unit}s` : `Every ${unit}`;
};

const adjustment = (
	type: string | null,
	value: string | null,
	currency: string | null,
): string | null => {
	if (!type || value === null) return null;
	if (type === "PERCENTAGE") return `${Number(value)}% off`;
	if (type === "FIXED_AMOUNT") return `${Number(value)} ${currency ?? ""} off`.trim();
	return `Set to ${Number(value)} ${currency ?? ""}`.trim();
};

/**
 * Shopify allows one fixed and one recurring pricing policy. A plan with a
 * single flat discount carries only the fixed one, so reading recurring alone
 * reports no discount on the most common setup there is.
 */
const discount = (plan: ProductSellingPlan): string => {
	const fixed = adjustment(
		plan.fixedAdjustmentType,
		plan.fixedAdjustmentValue,
		plan.fixedAdjustmentCurrency,
	);
	const recurring = adjustment(
		plan.recurringAdjustmentType,
		plan.recurringAdjustmentValue,
		plan.recurringAdjustmentCurrency,
	);

	if (fixed && recurring && fixed !== recurring) return `${fixed}, then ${recurring}`;
	return fixed ?? recurring ?? "None";
};

export const SellingPlans = ({ plans }: { plans: ProductSellingPlan[] }) => {
	if (plans.length === 0) return null;

	return (
		<section className="space-y-3">
			<h2 className="text-sm font-medium text-ink">Purchase options</h2>
			<div className="overflow-hidden rounded-md border border-line">
				<Table>
					<THead>
						<TR>
							<TH>Plan</TH>
							<TH className="w-40">Type</TH>
							<TH className="w-40">Billing</TH>
							<TH className="w-40">Delivery</TH>
							<TH className="w-36">Discount</TH>
						</TR>
					</THead>
					<TBody>
						{[...groupPlansByName(plans)].map(([groupName, groupPlans]) => (
							<Fragment key={groupName}>
								<TR className="bg-raised">
									<TD colSpan={5} className="py-2 text-xs font-medium text-ink-muted">
										{groupName}
									</TD>
								</TR>
								{groupPlans.map((plan) => (
									<TR key={plan.id}>
										<TD>{plan.name ?? "Untitled plan"}</TD>
										<TD>
											<Badge variant="quiet">
												{CATEGORY_LABELS[plan.category ?? ""] ?? plan.category ?? "Unknown"}
											</Badge>
										</TD>
										<TD className="text-ink-muted">
											{cadence(plan.billingInterval, plan.billingIntervalCount)}
										</TD>
										<TD className="text-ink-muted">
											{cadence(plan.deliveryInterval, plan.deliveryIntervalCount)}
										</TD>
										<TD className="text-ink-muted">{discount(plan)}</TD>
									</TR>
								))}
							</Fragment>
						))}
					</TBody>
				</Table>
			</div>
		</section>
	);
};
